/**
 * Scheduler AI Utility
 * Provides gap detection and smart scheduling suggestions
 */

import { getCreativeUsageStats, sortByFreshness } from './fatigueCalculator';

/**
 * Format a date to YYYY-MM-DD using local timezone (not UTC)
 * This prevents timezone issues where posts appear on wrong days
 * @param {Date} date - The date to format
 * @returns {string} - Date string in YYYY-MM-DD format
 */
const formatLocalDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Find gaps (empty days) in the schedule within a date range
 * @param {Array} scheduledPosts - Array of scheduled posts
 * @param {Date} startDate - Start of range
 * @param {Date} endDate - End of range
 * @returns {Array<Date>} - Array of dates with no scheduled content
 */
export const findGaps = (scheduledPosts, startDate, endDate) => {
    const gaps = [];
    const scheduledDates = new Set();

    // Build a set of scheduled date strings (YYYY-MM-DD format) using local timezone
    scheduledPosts.forEach(post => {
        if (post.scheduledDate) {
            const date = new Date(post.scheduledDate);
            const dateStr = formatLocalDate(date);
            scheduledDates.add(dateStr);
        }
    });

    // Check each day in the range
    const current = new Date(startDate);
    current.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    while (current <= end) {
        const dateStr = formatLocalDate(current);
        if (!scheduledDates.has(dateStr)) {
            gaps.push(new Date(current));
        }
        current.setDate(current.getDate() + 1);
    }

    return gaps;
};

/**
 * Calculate schedule coverage percentage
 * @param {Array} scheduledPosts - Array of scheduled posts
 * @param {number} daysAhead - Number of days to calculate coverage for
 * @returns {{percentage: number, covered: number, total: number}}
 */
export const calculateCoverage = (scheduledPosts, daysAhead = 30) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + daysAhead - 1);

    const gaps = findGaps(scheduledPosts, today, endDate);
    const covered = daysAhead - gaps.length;

    return {
        percentage: Math.round((covered / daysAhead) * 100),
        covered,
        total: daysAhead
    };
};

/**
 * Get suggested creatives for a specific date
 * Prioritizes low-fatigue creatives that haven't been used recently
 * @param {Date} targetDate - The date to suggest creatives for
 * @param {Array} creatives - All available creatives
 * @param {Array} scheduledPosts - All scheduled posts
 * @param {number} limit - Maximum number of suggestions
 * @returns {Array} - Sorted list of suggested creatives with usage stats
 */
export const getSuggestionsForDate = (targetDate, creatives, scheduledPosts, limit = 5) => {
    // Get usage stats for all creatives
    const creativesWithStats = creatives.map(creative => ({
        ...creative,
        ...getCreativeUsageStats(creative.id, scheduledPosts)
    }));

    // Filter out creatives already scheduled for this date (using local timezone)
    const targetDateStr = formatLocalDate(targetDate);
    const alreadyScheduledIds = new Set(
        scheduledPosts
            .filter(post => {
                if (!post.scheduledDate) return false;
                const postDateStr = formatLocalDate(new Date(post.scheduledDate));
                return postDateStr === targetDateStr;
            })
            .map(post => post.creativeId)
    );

    const availableCreatives = creativesWithStats.filter(
        creative => !alreadyScheduledIds.has(creative.id)
    );

    // Sort by freshness (lowest fatigue first)
    const sorted = sortByFreshness(availableCreatives);

    return sorted.slice(0, limit);
};

/**
 * Get overall scheduling recommendations
 * @param {Array} creatives - All creatives
 * @param {Array} scheduledPosts - All scheduled posts
 * @param {number} daysAhead - Days to analyze
 * @returns {{gaps: Array, recommendations: Array, coverage: object}}
 */
export const getSchedulingRecommendations = (creatives, scheduledPosts, daysAhead = 30) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + daysAhead - 1);

    const gaps = findGaps(scheduledPosts, today, endDate);
    const coverage = calculateCoverage(scheduledPosts, daysAhead);

    // Generate recommendations for the first few gaps
    const recommendations = gaps.slice(0, 5).map(gapDate => ({
        date: gapDate,
        suggestions: getSuggestionsForDate(gapDate, creatives, scheduledPosts, 3)
    }));

    return {
        gaps,
        recommendations,
        coverage
    };
};

/**
 * Check if a specific date has scheduled content
 * @param {Date} date - The date to check
 * @param {Array} scheduledPosts - All scheduled posts
 * @returns {boolean}
 */
export const hasScheduledContent = (date, scheduledPosts) => {
    const dateStr = formatLocalDate(date);

    return scheduledPosts.some(post => {
        if (!post.scheduledDate) return false;
        const postDateStr = formatLocalDate(new Date(post.scheduledDate));
        return postDateStr === dateStr;
    });
};

/**
 * Get scheduled content for a specific date
 * @param {Date} date - The date to get content for
 * @param {Array} scheduledPosts - All scheduled posts
 * @param {Array} creatives - All creatives (to enrich with creative data)
 * @returns {Array} - Scheduled posts with creative data
 */
export const getContentForDate = (date, scheduledPosts, creatives) => {
    const dateStr = formatLocalDate(date);

    const postsForDate = scheduledPosts.filter(post => {
        if (!post.scheduledDate) return false;
        const postDateStr = formatLocalDate(new Date(post.scheduledDate));
        return postDateStr === dateStr;
    });

    // Enrich with creative data
    return postsForDate.map(post => {
        const creative = creatives.find(c => c.id === post.creativeId);
        return {
            ...post,
            creative: creative || null
        };
    });
};

/**
 * Get the next available gap date
 * @param {Array} scheduledPosts - All scheduled posts
 * @param {number} maxDaysAhead - Maximum days to look ahead
 * @returns {Date|null} - Next gap date or null if none found
 */
export const getNextGapDate = (scheduledPosts, maxDaysAhead = 30) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + maxDaysAhead - 1);

    const gaps = findGaps(scheduledPosts, today, endDate);

    return gaps.length > 0 ? gaps[0] : null;
};
